import { DateString, ResultOption } from "../../lib/api/models";
import {
  centsToDollarString,
  convertDateStringToMonthDayYearFormat,
  dollarStringToCents,
  formatToDateStringInCurrentTimezone,
} from "../../lib/formatting";
import { adjustDateByDays, strIncludes } from "../../lib/util";
import { resultQuickClickOptions } from "./resultConstants";
import {
  CsrNoteData,
  TabNoteData,
  PaymentNoteData,
  TabResultData,
  PaymentData,
  NoPaymentData,
  ResultQuickClickOption,
  Describable,
} from "./resultTypes";
import { parse } from "date-fns/parse";
import { isSameDay } from "date-fns";

export function paymentMethodRequiresCallBack(
  paymentMethod: ResultOption | null,
) {
  if (paymentMethod === null) {
    return false;
  }
  return paymentMethod.id === "B2CCB" || paymentMethod.id === "C2CCB";
}

// CONTACT: [Contact Type] PERM: [P2D] ACTIVITY: [Activity], [Activity, ...] TRANSFER: [Transfer]
function getCsrNote(data: CsrNoteData) {
  let note = "";

  if (data.contactPerson) {
    note += `CONTACT: ${data.contactPerson.value} `;
  }

  if (data.permissionToDiscuss !== null) {
    note += `PERM: ${data.permissionToDiscuss ? "YES" : "NO"} `;
  }

  if (data.activities.length > 0) {
    note += `ACTIVITY: ${data.activities.map((v) => v.value).join(", ")} `;
  }

  if (data.transfer) {
    note += `TRANSFER: ${data.transfer.value}`;
  }

  return note;
}

// [Contact Type] [Call Reason (optional)] [Transfer Source (optional)]
// COMPLETE [Action Code]
// FOR $[Payment Amount Due] VIA [Payment Method]
// DATED FOR [Payment Method Date]
// (optional) TO CB ON [Call Back Date]
function getPaymentNote(data: PaymentNoteData) {
  let note = "";

  if (data.contactPerson) {
    note += `${data.contactPerson.value} `;
  }

  if (data.inboundReason) {
    note += `${data.inboundReason.value} `;
  }

  if (data.transferSource) {
    note += `${data.transferSource.value} `;
  }

  note += "COMPLETE ";

  if (data.actionCode) {
    note += `${data.actionCode.value} `;
  }

  note += "FOR $";

  try {
    const paymentAmountCents = dollarStringToCents(data.paymentAmount);
    if (paymentAmountCents && !isNaN(paymentAmountCents)) {
      note += `${centsToDollarString(paymentAmountCents, true, true)}`;
    }
  } catch {
    /* Payment amount cannot be validated, do not add to result note */
  }

  note += " VIA ";

  if (data.paymentMethod) {
    note += `${data.paymentMethod.value} `;
  }

  note += `DATED FOR `;

  if (data.paymentDate) {
    note += `${convertDateStringToMonthDayYearFormat(data.paymentDate, "")} `;
  }

  if (paymentMethodRequiresCallBack(data.paymentMethod)) {
    note += `TO CB ON `;
  }

  if (data.callBackDate) {
    note += convertDateStringToMonthDayYearFormat(data.callBackDate, "");
  }

  return note;
}

export function getResultNote({ tab, data }: TabNoteData): string {
  switch (tab) {
    case "CSR":
      return getCsrNote(data);
    case "PAYMENT":
      return getPaymentNote(data);
    case "NOPAYMENT":
      return "";
  }
}

// [Action Cd][13 digit payment amount][mmdd]
function getPaymentActionEntry(data: PaymentData) {
  let actionEntry = data.actionCode?.id ?? "";

  try {
    const paymentAmountCents = dollarStringToCents(data.paymentAmount);
    if (paymentAmountCents && !isNaN(paymentAmountCents)) {
      actionEntry += Math.floor(paymentAmountCents / 100)
        .toString()
        .padStart(13, "0");
    }
  } catch {
    /* Payment amount cannot be validated, do not add to action entry */
  }

  const [_, month, day] = data.promiseDate.split("-");
  actionEntry += month ?? "";
  actionEntry += day ?? "";

  return actionEntry;
}

// [Action Cd][Correspondence Reason (optional)][Call Reason (optional)][Transfer Source (optional)]
// [Contact Detail / "Who"][What / "Other"][Action Note]
function getNoPaymentActionEntry(data: NoPaymentData) {
  return `${data.actionCode?.id ?? ""}${data.correspondenceReason?.id ?? ""}${data.inboundReason?.id ?? ""}${data.transferSource?.id ?? ""}${data.contactDetail ? data.contactDetail.id : !data.contactPerson || data.contactPerson?.id === "NONE" ? "" : data.contactPerson?.id}${data.other?.id ?? ""}${data.actionNote}`;
}

export function getResultActionEntry({ tab, data }: TabResultData): string {
  switch (tab) {
    case "CSR":
      return "";
    case "PAYMENT":
      return getPaymentActionEntry(data);
    case "NOPAYMENT":
      return getNoPaymentActionEntry(data);
  }
}

export function getQuickClickOptions(): ResultQuickClickOption[] {
  return resultQuickClickOptions;
}

export function filterDescribable<T extends Describable>(
  option: T,
  value: string,
) {
  if (!value) {
    return true;
  }
  return strIncludes(option.description, value);
}

export function getNoPaymentWorkDateDefault(): DateString {
  return formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 1));
}

export function getNoPaymentWorkDateMinMax(): {
  min: DateString;
  max: DateString;
} {
  return {
    min: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 1)),
    max: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 30)),
  };
}

type ResultPaymentDateDefault = Partial<
  Pick<PaymentData, "paymentDate" | "promiseDate" | "workDate">
>;

export function getResultPaymentDateDefault(
  data: Pick<
    PaymentData,
    | "paymentMethod"
    | "callBackDate"
    | "paymentDate"
    | "promiseDate"
    | "workDate"
  >,
  clearAll?: boolean | undefined,
): ResultPaymentDateDefault {
  const today = formatToDateStringInCurrentTimezone(new Date());

  const paymentDate: DateString = clearAll === true ? "" : data.paymentDate;
  const callBackDate: DateString = clearAll === true ? "" : data.callBackDate;

  const parsedPaymentDate = paymentDate
    ? parse(paymentDate, "yyyy-MM-dd", new Date())
    : null;
  const parsedCallBackDate = callBackDate
    ? parse(callBackDate, "yyyy-MM-dd", new Date())
    : null;

  const result: ResultPaymentDateDefault =
    clearAll === true
      ? {
          paymentDate: "",
          promiseDate: "",
          workDate: "",
        }
      : {};

  switch (data.paymentMethod?.id) {
    case "B2CAGNT":
    case "C2C":
    case "B2CVRU":
    case "WEB":
      result.paymentDate = paymentDate ? paymentDate : today;
      result.promiseDate = formatToDateStringInCurrentTimezone(
        adjustDateByDays(parsedPaymentDate ?? new Date(), 2, true),
      );
      result.workDate = formatToDateStringInCurrentTimezone(
        adjustDateByDays(parsedPaymentDate ?? new Date(), 3, true),
      );
      break;
    case "B2CCB":
    case "C2CCB":
      result.promiseDate =
        parsedPaymentDate !== null
          ? formatToDateStringInCurrentTimezone(
              adjustDateByDays(parsedPaymentDate, 2, true),
            )
          : undefined;
      result.workDate =
        parsedCallBackDate !== null
          ? formatToDateStringInCurrentTimezone(
              adjustDateByDays(parsedCallBackDate, 1),
            )
          : undefined;
      break;
    case "WU":
    case "WIRE":
    case "OVRNGT":
      result.promiseDate =
        parsedPaymentDate !== null
          ? formatToDateStringInCurrentTimezone(
              adjustDateByDays(parsedPaymentDate, 2, true),
            )
          : undefined;
      result.workDate =
        parsedPaymentDate !== null
          ? isSameDay(parsedPaymentDate, new Date())
            ? formatToDateStringInCurrentTimezone(
                adjustDateByDays(parsedPaymentDate, 1),
              )
            : paymentDate
          : undefined;
      break;
    case "MAIL":
      result.promiseDate =
        parsedPaymentDate !== null
          ? formatToDateStringInCurrentTimezone(
              adjustDateByDays(parsedPaymentDate, 5, true),
            )
          : undefined;
      result.workDate =
        parsedPaymentDate !== null
          ? formatToDateStringInCurrentTimezone(
              adjustDateByDays(parsedPaymentDate, 5, true),
            )
          : undefined;
      break;
  }

  // If value is undefined, remove so it does not set on spread
  if (result.paymentDate === undefined) {
    delete result.paymentDate;
  }

  // If value is undefined, remove so it does not set on spread
  if (result.promiseDate === undefined) {
    delete result.promiseDate;
  }

  // If value is undefined, remove so it does not set on spread
  if (result.workDate === undefined) {
    delete result.workDate;
  }

  return result;
}

export function getPaymentDateMinMax(data: PaymentData): {
  min?: DateString;
  max?: DateString;
} {
  const today = formatToDateStringInCurrentTimezone(new Date());

  if (data.actionCode?.id === "PP") {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "B2CCB":
      case "C2C":
      case "C2CCB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 30),
          ),
        };
      case "B2CVRU":
      case "WEB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 5, true),
          ),
        };
      case "WU":
      case "WIRE":
      case "OVRNGT":
        return {
          min: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), -5),
          ),
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 4, true),
          ),
        };
      case "MAIL":
        return {
          min: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), -5),
          ),
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 1, true),
          ),
        };
      case "OTHER":
        return {
          min: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), -5),
          ),
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 5, true),
          ),
        };
    }
  }

  if (
    data.actionCode?.id === "AP" ||
    data.actionCode?.id === "PC" ||
    data.actionCode?.id === "OC"
  ) {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "B2CCB":
      case "C2C":
      case "C2CCB":
      case "WU":
      case "WIRE":
      case "OVRNGT":
      case "MAIL":
      case "OTHER":
      case "B2CVRU":
      case "WEB":
        return {
          min: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), -45),
          ),
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 30),
          ),
        };
    }
  }

  return {
    min: today,
    max: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 30)),
  };
}

export function getPromiseDateMinMax(data: PaymentData): {
  min?: DateString;
  max?: DateString;
} {
  const today = formatToDateStringInCurrentTimezone(new Date());

  if (data.actionCode?.id === "PP") {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "B2CCB":
      case "C2C":
      case "C2CCB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 30),
          ),
        };
      case "MAIL":
      case "OTHER":
      case "B2CVRU":
      case "WEB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 5, true),
          ),
        };
      case "WU":
      case "WIRE":
      case "OVRNGT":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 6, true),
          ),
        };
    }
  }

  if (
    data.actionCode?.id === "AP" ||
    data.actionCode?.id === "PC" ||
    data.actionCode?.id === "OC"
  ) {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "B2CCB":
      case "C2C":
      case "C2CCB":
      case "WU":
      case "WIRE":
      case "OVRNGT":
      case "MAIL":
      case "OTHER":
      case "B2CVRU":
      case "WEB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 45),
          ),
        };
    }
  }

  return {
    min: today,
    max: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 30)),
  };
}

export function getWorkDateMinMax(_data: PaymentData): {
  min?: DateString;
  max?: DateString;
} {
  return {
    min: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 1)),
    max: formatToDateStringInCurrentTimezone(adjustDateByDays(new Date(), 30)),
  };
}

export function getCallBackDateMinMax(data: PaymentData): {
  min?: DateString;
  max?: DateString;
} {
  const today = formatToDateStringInCurrentTimezone(new Date());

  if (data.actionCode?.id === "PP") {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "C2C":
      case "WU":
      case "WIRE":
      case "OVRNGT":
      case "MAIL":
      case "OTHER":
      case "B2CVRU":
      case "WEB":
        return {};
      case "B2CCB":
      case "C2CCB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 4, true),
          ),
        };
    }
  }

  if (
    data.actionCode?.id === "AP" ||
    data.actionCode?.id === "PC" ||
    data.actionCode?.id === "OC"
  ) {
    switch (data.paymentMethod?.id) {
      case "B2CAGNT":
      case "C2C":
      case "WU":
      case "WIRE":
      case "OVRNGT":
      case "MAIL":
      case "OTHER":
      case "B2CVRU":
      case "WEB":
        return {};
      case "B2CCB":
      case "C2CCB":
        return {
          min: today,
          max: formatToDateStringInCurrentTimezone(
            adjustDateByDays(new Date(), 45),
          ),
        };
    }
  }

  return {
    min: today,
    max: formatToDateStringInCurrentTimezone(
      adjustDateByDays(new Date(), 4, true),
    ),
  };
}
